import React, { useState, useMemo } from "react";
import { View } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { AppText } from "@/components/AppText";
import { FlightDelayWithRefundProcess } from "@/types/flightReservation";
import { PhoneRefundFlowModal } from "../hotelReservation/PhoneRefundFlowModal";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { Colors } from "@/constants/Colors";
import Icons from "@/assets/icons";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { useTrackImpression, useTrackClick } from "@/hooks/tracking";

type Props = {
  reservation: FlightDelayWithRefundProcess;
};

export const FlightReservationCard: React.FC<Props> = ({ reservation }) => {
  const [showRefundModal, setShowRefundModal] = useState(false);

  const platformName = reservation.airline || "Unknown";

  // Get the compensation amount from the delay notice
  const displayAmount = useMemo(() => {
    return reservation.delayNotice?.expectedCompensationInDollar || 0;
  }, [reservation.delayNotice?.expectedCompensationInDollar]);

  // Calculate the status text to display
  const statusText = useMemo(() => {
    if (reservation.delayNotice && reservation.delayNotice.delayMinutes) {
      const hours = Math.floor(reservation.delayNotice.delayMinutes / 60);
      const minutes = reservation.delayNotice.delayMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return `Flight delayed ${hours}h ${minutes}m - compensation available`;
      } else if (hours > 0) {
        return `Flight delayed ${hours} hour${
          hours > 1 ? "s" : ""
        } - compensation available`;
      } else {
        return `Flight delayed ${minutes} minutes - compensation available`;
      }
    }
    return "No delays found.";
  }, [reservation.delayNotice?.delayMinutes]);

  // Track when card is shown
  useTrackImpression(TRACKING_EVENTS.FLIGHT_REFUND_SHOWN, {
    airline: reservation.airline,
    amount: displayAmount,
  });

  // Track when "Claim" button is clicked
  const handleStartRefund = useTrackClick(
    TRACKING_EVENTS.FLIGHT_REFUND_CLICKED,
    {
      airline: reservation.airline,
      amount: displayAmount,
    }
  )(() => {
    setShowRefundModal(true);
  });

  const handleCloseModals = () => {
    setShowRefundModal(false);
  };

  return (
    <View style={styles.card}>
      {/* Header section - matches OfferCardHeader layout */}
      <View style={styles.header}>
        {/* Left side - Airline logo */}
        <View style={styles.imageContainer}>
          <View style={styles.airlineLogo}>
            <AppText style={styles.airlineLogoText}>
              {reservation.airline?.substring(0, 2).toUpperCase() || "FL"}
            </AppText>
          </View>
        </View>

        {/* Middle - Title and Amount */}
        <View style={styles.leftContent}>
          <AppText style={styles.description} numberOfLines={2}>
            Delay compensation on {platformName} for{" "}
            {reservation.departureAirport} → {reservation.arrivalAirport}
          </AppText>
          {displayAmount > 0 && (
            <AppText style={styles.amountText} numberOfLines={2}>
              ${displayAmount.toFixed(0)}
            </AppText>
          )}
        </View>

        {/* Right side - Tags */}
        <View style={styles.tagsContainer}>
          {/* No tags for flight reservations */}
        </View>
      </View>

      {/* Actions section - matches OfferCardActions layout */}
      <View style={styles.actions}>
        <View style={styles.noteContainer}>
          <Icons.ClockGray />
          <AppText style={styles.noteText} numberOfLines={2}>
            {statusText}
          </AppText>
        </View>
        <ActionButton
          title="Claim"
          type="primary"
          onPress={handleStartRefund}
          style={styles.claimButton}
          textStyle={styles.claimButtonText}
        />
      </View>

      <PhoneRefundFlowModal
        visible={showRefundModal}
        onClose={handleCloseModals}
        item={reservation}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderColor: "#F3F3F3",
    padding: 16,
    gap: 16,
  },
  leftContent: {
    flex: 1,
  },
  tagsContainer: {
    flexDirection: "row",
    alignSelf: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  amountText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 20,
    color: Colors.text,
  },
  description: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    color: "#161D14",
  },
  imageContainer: {
    width: 80,
    height: 80,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F7F8FA",
  },
  airlineLogo: {
    width: "100%",
    height: "100%",
    borderRadius: 16,
    backgroundColor: "#F0F0F0",
    justifyContent: "center",
    alignItems: "center",
  },
  airlineLogoText: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.Bold,
    color: "#666666",
  },
  actions: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 12,
    justifyContent: "space-between",
  },
  noteContainer: {
    flexShrink: 1,
    flexDirection: "row",
    gap: 6,
    alignItems: "center",
    justifyContent: "flex-start",
    paddingLeft: 4,
  },
  noteText: {
    fontFamily: FontFamilies.InterTight.Regular,
    fontSize: 13,
    color: "#000000",
  },
  claimButton: {
    height: 39,
    minWidth: 96,
    paddingHorizontal: 16,
  },
  claimButtonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
  },
});
